import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';
import AppContext from '../contexts/AppContext';

import checkIcon from '../images/checkIcon.svg';
import withLeftSideBar from '../hocs/withLeftSideBar';
import IconButton from '../components/IconButton';
import { RoundButton } from '../components/Buttons';
import HeroOrderSplitButton from '../components/HeroOrderSplitButton';
import Spinner from '../components/Spinner';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';
import {
  MainHeader,
  MainContentTopBar,
  MainContentTopBarButtons,
  ContainerWrapper,
} from '../components/basicComponents';
import TranslationsTable from '../components/TranslationsTable';
import i18n from '../i18n';
import { getTranslations } from '../services/translationsService';
import { showError } from '../services/toastService';
import { PATH_ROUTES } from '../utils/pathRoutes';
import { formatDate } from '../utils/dateUtils';
import { TRANSLATION_STATUS } from '../utils/translationStatus';
import { canAccessTranslations } from '../utils/translationAccess';
import { COL, TABLES, resolveColumns } from '../config/tableColumns';

const StyledTranslationsContainer = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  flex-direction: column;
`;

const StatusText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ statusColor }) => statusColor};
  display: inline-flex;
  align-items: center;
`;

const StatusIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const DeliveryPrefix = styled.span`
  display: inline-block;
  width: 1.2em;
`;

const mapTranslation = (t) => ({
  id: t._id,
  orderNumber: t.orderNumber,
  orderDate: formatDate(t.createdAt),
  orderDateRaw: t.createdAt ? new Date(t.createdAt).getTime() : undefined,
  userGroup: t.invoiceUserGroupName || '',
  customer: t.customerName || '',
  fromLanguage: t.sourceLanguageNameEn,
  toLanguage: t.targetLanguageNameEn,
  username: t.interpreterName || '',
  delivery: formatDate(t.dueDate),
  deliveryRaw: t.dueDate ? new Date(t.dueDate).getTime() : undefined,
  deliveryPrefix: t.status !== TRANSLATION_STATUS.DELIVERED ? '~ ' : '',
  status: t.status,
});

const Translations = ({ toggleSideBar, sidebarDocked, ...rest }) => {
  const { colors, layout } = useContext(ThemeContext);
  const { sessionExpired } = useContext(FunctionsContext);
  const { user } = useContext(AppContext);
  const history = useHistory();

  const translationEnabled = canAccessTranslations(user);

  const [loading, setLoading] = useState(true);
  const [translations, setTranslations] = useState([]);

  // Customers who wouldn't see Translations in the sidebar shouldn't reach the
  // translations list even by typing the URL directly — send them home.
  useEffect(() => {
    if (!translationEnabled) {
      history.replace(PATH_ROUTES.home);
    }
  }, [translationEnabled, history]);

  useEffect(() => {
    if (!translationEnabled) {
      return undefined;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        const data = await getTranslations();
        if (!cancelled) {
          const list = Array.isArray(data) ? data : (data.translations || []);
          setTranslations(list.map(mapTranslation));
          document.dispatchEvent(new CustomEvent('updateTranslationsCount', {
            detail: { count: list.length }
          }));
        }
      } catch (e) {
        if (e.message === 'Session expired') {
          sessionExpired();
        } else {
          showError(i18n('requestErrorMessage'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getStatusColor = (status) => {
    switch (status) {
      case TRANSLATION_STATUS.NEW_ORDER:
        return colors.heroAccentDark;
      case TRANSLATION_STATUS.ASSIGNED:
      case TRANSLATION_STATUS.IN_PROGRESS:
      case TRANSLATION_STATUS.COMPLETED: // completed is shown as "in progress" to customers
        return colors.heroPurple80;
      case TRANSLATION_STATUS.DELIVERED:
        return colors.webDarkBlue;
      case TRANSLATION_STATUS.CANCELLED:
        return colors.webGray;
      default:
        return colors.webGray;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case TRANSLATION_STATUS.NEW_ORDER:
        return i18n('translation_status_new_order');
      case TRANSLATION_STATUS.ASSIGNED:
        return i18n('translation_status_assigned');
      case TRANSLATION_STATUS.IN_PROGRESS:
      case TRANSLATION_STATUS.COMPLETED: // completed is shown as "in progress" to customers
        return i18n('translation_status_in_progress');
      case TRANSLATION_STATUS.DELIVERED:
        return i18n('translation_status_delivered');
      case TRANSLATION_STATUS.CANCELLED:
        return i18n('translation_status_cancelled');
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    if (status === TRANSLATION_STATUS.DELIVERED) {
      return <StatusIcon><img src={checkIcon} alt="" width="16" height="16" /></StatusIcon>;
    }
    return <StatusIcon aria-hidden="true" />;
  };

  const columns = useMemo(() => {
    const definitions = {
      [COL.TR_ORDER_NUMBER]: {
        key: 'orderNumber',
        label: i18n('translation_order_number'),
        render: (val) => `# ${val}`,
      },
      [COL.TR_ORDER_DATE]: {
        key: 'orderDate',
        label: i18n('translation_order_date'),
        sortKey: 'orderDateRaw',
      },
      [COL.TR_DELIVERY_DATE]: {
        key: 'delivery',
        label: i18n('translation_delivery'),
        sortKey: 'deliveryRaw',
        render: (val, row) => (
          <>
            <DeliveryPrefix>{row.deliveryPrefix?.trim() || ''}</DeliveryPrefix>
            {val}
          </>
        ),
      },
      [COL.TR_USER_GROUP]: { key: 'userGroup', label: i18n('translation_user_group') },
      [COL.TR_FROM]: { key: 'fromLanguage', label: i18n('translation_from') },
      [COL.TR_TO]: { key: 'toLanguage', label: i18n('translation_to') },
      [COL.TR_TRANSLATOR]: { key: 'username', label: i18n('translation_username') },
      [COL.TR_CUSTOMER]: { key: 'customer', label: i18n('translation_customer') },
      [COL.TR_STATUS]: {
        key: 'status',
        label: i18n('translation_status'),
        render: (val) => (
          <StatusText statusColor={getStatusColor(val)}>
            {getStatusIcon(val)}
            {getStatusLabel(val)}
          </StatusText>
        ),
      },
    };

    return resolveColumns(TABLES.TRANSLATIONS, definitions);
  }, [colors]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = (translation) => {
    history.push(`/translations/${translation.id}`);
  };

  const handleOrderTranslation = () => {
    history.push(PATH_ROUTES.translationOrder);
  };

  if (!translationEnabled) {
    return null;
  }

  return (
    <StyledTranslationsContainer {...rest}>
      <MainContentTopBar
        borderBottomColor={colors.webDeviderColor}
      >
        {sidebarDocked ? null : (
          <IconButton
            icon={'menu'}
            iconLib={'material'}
            iconColor={colors.tulkaMainColor}
            onClick={toggleSideBar}
          />
        )}
        <MainHeader
          sidebarDocked={sidebarDocked}
          padding={layout.padding}
          color={colors.webDarkBlue}
        >
          {i18n('translations')}
        </MainHeader>
        <MainContentTopBarButtons>
          {isHeroBrandEnabled() ? (
            <HeroOrderSplitButton dataTest="translationsOrderSplitButton" />
          ) : (
            <RoundButton
              label={i18n('order_translation')}
              onClick={handleOrderTranslation}
              smallButton={true}
              darkButton={true}
            />
          )}
        </MainContentTopBarButtons>
      </MainContentTopBar>
      <ContainerWrapper>
        <TranslationsTable
          columns={columns}
          data={translations}
          onRowOpen={handleOpen}
        />
      </ContainerWrapper>
      {loading && <Spinner overlay />}
    </StyledTranslationsContainer>
  );
};

export default withLeftSideBar(Translations);
